// frontend/src/components/upgradeOfferText.js

/**
 * Upgrade-offer copy - the ONE place every string of the trial-conversion
 * selling surfaces lives (navbar pill, dashboard offer banner, checkout).
 *
 * WHY A SEPARATE MODULE. Three components on three different surfaces render
 * the same offer. The owner approves wording per surface and per string, and a
 * price/percent claim is a public statement: it must be reviewable in one
 * screenful, not hunted across a navbar, a banner and a checkout page.
 *
 * STATUS: every value below is approved wording used VERBATIM - the owner's
 * Hebrew of 2026-09-18 and its delegated English twin, ids CD-01..CD-09 of the
 * approved copy sheet. Both language paths are real; nothing here is a
 * placeholder any more.
 *
 * COPY RULES that bind whoever writes these (CLAUDE.md):
 *   - ASCII hyphen `-` only. Never an em/en dash.
 *   - In-app UI says "הזדמנות"/"הזדמנויות", never "ליד".
 *   - Percentages can be fractional (1.5% x days, so 10.5% / 19.5% are real) -
 *     the wording must read correctly with one decimal place.
 *   - No "days lost" framing anywhere (owner decision): the checkout shows a
 *     struck price and a discounted price, nothing about forfeited trial days.
 */

/**
 * The live 1-3-days-left body, for the path where there is no offer end to
 * state. Owner-approved and already shipping in SubscriptionBanner's offer-less
 * branch; CD-07 falls back to it rather than printing half a sentence.
 */
const LAST_DAYS_BODY_NO_OFFER = {
  he: "שדרג עכשיו כדי לא לאבד גישה להזדמנויות.",
  en: "Upgrade now to keep your opportunities access.",
};

/**
 * The currency marker appended to a bare amount in CD-01 and CD-08 - one per
 * language, and they DELIBERATELY DIFFER.
 *
 * CD-08 is the reason. It is not visible copy: it is the accessible name of a
 * struck price whose digits are `aria-hidden`, so its only job is to be SPOKEN.
 * An English screen-reader voice says "N-I-S" correctly and makes a mess of
 * both `ש"ח` and `₪`; a Hebrew voice reads `ש"ח` correctly. So the marker
 * follows the voice, not the glyph.
 *
 * SEPARATE FROM THE PRICE GLYPH ON THE CARDS. The plan cards render `199₪` in
 * both languages and keep doing so - that is app-wide numeric convention, not
 * copy, and nothing here touches it. These two constants are only ever used
 * INSIDE a sentence.
 */
const CURRENCY_SUFFIX_HE = 'ש"ח';
const CURRENCY_SUFFIX_EN = "NIS";

/**
 * Every string of the feature, keyed by the spec's binding CD-xx id.
 *
 * Each entry is a FUNCTION even where the final copy is a constant: the
 * parameters document exactly which numbers the owner may use, and a call site
 * that already passes them needs no edit later.
 *
 * `isHebrew` picks the language. Both branches are approved wording; neither is
 * a fallback for the other, so a missing branch would be a bug rather than a
 * degradation - which is why every entry returns one or the other explicitly.
 */
export const upgradeOfferCopy = {
  /**
   * CD-01 - navbar upgrade pill, when a discount exists.
   *
   * The plain "שדרג" label for the no-offer case is NOT here: it is the
   * navbar's own live string, and the pill only calls this function once it has
   * a positive saving.
   *
   * @param {object} p
   * @param {boolean} p.isHebrew
   * @param {number} p.savingNis whole shekels off the Professional first month
   * @param {number} p.discountPercent e.g. 10.5
   */
  navbarPillWithSaving: ({ isHebrew, savingNis, discountPercent }) =>
    isHebrew
      ? `שדרג - חיסכון עד ${savingNis} ${CURRENCY_SUFFIX_HE}`
      : `Upgrade - save up to ${savingNis} ${CURRENCY_SUFFIX_EN}`,

  /**
   * CD-02 - dashboard offer banner title (>3 trial days left, shown only after
   * the user's first hot opportunity).
   *
   * `endsAt` is OPTIONAL and the deadline clause is dropped when it is missing,
   * the same rule `formatOfferEnd` documents for its own "" return: a headline
   * that cannot state a real deadline states none, rather than "עד undefined".
   *
   * It is a DATE, not a date+time: the approved wording says "בשדרוג עד {date}"
   * with no `{time}` field, so the caller passes `formatOfferEndDate`, never
   * `formatOfferEnd` (which is the checkout's date+time pair, CD-09).
   *
   * @param {object} p
   * @param {boolean} p.isHebrew
   * @param {number|string} p.discountPercent already formatted, e.g. "10.5"
   * @param {string} [p.endsAt] offer end DATE, from `formatOfferEndDate`
   */
  offerBannerTitle: ({ isHebrew, discountPercent, endsAt }) => {
    if (isHebrew) {
      return endsAt
        ? `${discountPercent}% הנחה על החודש הראשון - בשדרוג עד ${endsAt}`
        : `${discountPercent}% הנחה על החודש הראשון`;
    }
    return endsAt
      ? `${discountPercent}% off your first month - upgrade by ${endsAt}`
      : `${discountPercent}% off your first month`;
  },

  /**
   * CD-03 - dashboard offer banner body. A constant by the owner's wording: the
   * numbers live in the title, the body carries the reason to act.
   * @param {object} p
   * @param {boolean} p.isHebrew
   * @param {number|string} p.discountPercent
   * @param {string} p.endsAt offer end, already formatted in Asia/Jerusalem
   * @param {number} p.savingNis whole shekels off the Professional first month
   */
  offerBannerBody: ({ isHebrew, discountPercent, endsAt, savingNis }) =>
    isHebrew
      ? "שדרוג עכשיו שומר את כל ההגדרות וההתראות שלך, והחודש הראשון במחיר מוזל. ההנחה קטנה בכל יום שעובר."
      : "Upgrading now keeps all your settings and alerts, and your first month is discounted. The discount shrinks every day.",

  /** CD-04 - dashboard offer banner CTA label. */
  offerBannerCta: ({ isHebrew }) =>
    isHebrew ? "לשדרוג בהנחה" : "Upgrade with the discount",

  /**
   * CD-05 - the offer banner's dismiss control label.
   *
   * A VISIBLE label, not an `aria-label` (the spec calls it an aria-label; see
   * the note in SubscriptionBanner for why the control became a labelled text
   * button instead of a corner X). It is therefore both the accessible name and
   * the on-screen words - one word, per the approved wording, which is what
   * keeps WCAG 2.5.3 (Label in Name) satisfied with no aria-label beside it.
   */
  offerBannerDismissLabel: ({ isHebrew }) => (isHebrew ? "סגירה" : "Dismiss"),

  /**
   * CD-06 - title of the 1-3-days-left banner, re-led with "keep what you have".
   *
   * NOT RENDERED UNTIL AN OFFER IS PRESENT. The 1-3 day banner is already LIVE
   * with owner-approved wording, and SubscriptionBanner keeps those live
   * strings whenever there is no offer (which is also the fail-closed path when
   * the offer endpoint is down); CD-06/CD-07 are used only on the branch that
   * carries offer numbers.
   *
   * SINGULAR AND PLURAL ARE TWO SENTENCES, not one with a number in it. At
   * exactly one day the Hebrew counts the day as a word ("עוד יום אחד") and
   * drops the plural verb agreement, and the English drops its "s" - "עוד 1
   * ימים" and "1 days" are both wrong, and both are what a single template
   * produces. The boundary is 1 vs 2 and it is test-pinned in both languages.
   *
   * @param {object} p
   * @param {boolean} p.isHebrew
   * @param {number} p.days whole days left in the trial (1..3)
   */
  lastDaysBannerTitle: ({ isHebrew, days }) => {
    if (isHebrew) {
      return days === 1
        ? "עוד יום אחד והסריקה נעצרת - הזדמנויות חדשות שיזוהו כבר לא יגיעו אליך"
        : `עוד ${days} ימים והסריקה נעצרת - הזדמנויות חדשות שיזוהו כבר לא יגיעו אליך`;
    }
    return days === 1
      ? "1 day left before scanning stops - newly found opportunities will no longer reach you"
      : `${days} days left before scanning stops - newly found opportunities will no longer reach you`;
  },

  /**
   * CD-07 - body of the 1-3-days-left banner, carrying the offer numbers.
   *
   * Falls back to the live no-offer body when the formatted end date is
   * missing: the approved sentence is built around a stated deadline, so
   * without one there is no approved wording to render.
   *
   * `endsAt` is a DATE here, like CD-02's and for the same reason - the
   * approved wording is "שדרוג עד {date}", with no `{time}` field.
   *
   * @param {object} p
   * @param {boolean} p.isHebrew
   * @param {number} p.days
   * @param {number|string} p.discountPercent already formatted, e.g. "10.5"
   * @param {string} p.endsAt offer end DATE, from `formatOfferEndDate`
   * @param {number} p.savingNis
   */
  lastDaysBannerBody: ({ isHebrew, days, discountPercent, endsAt, savingNis }) => {
    if (!endsAt) return isHebrew ? LAST_DAYS_BODY_NO_OFFER.he : LAST_DAYS_BODY_NO_OFFER.en;
    return isHebrew
      ? `שדרוג עד ${endsAt} שומר את כל ההגדרות וההתראות שלך ומזכה ב-${discountPercent}% הנחה על החודש הראשון.`
      : `Upgrade by ${endsAt} to keep all your settings and alerts, and get ${discountPercent}% off your first month.`;
  },

  /**
   * CD-08 - accessible name for the struck-through full price on a plan card.
   * The struck number itself is `aria-hidden`; this is what is announced.
   *
   * The currency comes from `CURRENCY_SUFFIX_*`, not from the `currency`
   * parameter: that parameter carries the glyph `₪` the SIGHTED card shows,
   * while this string exists only to be read aloud - which is exactly why the
   * two markers differ by language. `currency` stays in the signature because
   * it is what the sighted number beside it renders.
   *
   * @param {object} p
   * @param {boolean} p.isHebrew
   * @param {number} p.fullPrice
   * @param {number} p.finalPrice
   * @param {string} p.currency
   */
  checkoutStrikePriceLabel: ({ isHebrew, fullPrice, finalPrice, currency }) =>
    isHebrew
      ? `מחיר מלא ${fullPrice} ${CURRENCY_SUFFIX_HE}`
      : `Full price ${fullPrice} ${CURRENCY_SUFFIX_EN}`,

  /**
   * CD-09 - the checkout offer line under the plan cards: percent + the offer's
   * explicit end, in Asia/Jerusalem.
   *
   * BOTH languages get their `{date}` and `{time}` fields from ONE string, and
   * the locale supplies the joining word: `formatOfferEnd` renders
   * "24 בספטמבר בשעה 12:00" under he-IL and "24 September at 12:00" under
   * en-GB, which is exactly the pair each approved sentence spells
   * ("בתוקף עד {date} בשעה {time}" / "valid until {date} at {time}").
   * Nothing here re-formats or re-joins a date.
   *
   * @param {object} p
   * @param {boolean} p.isHebrew
   * @param {number|string} p.discountPercent already formatted, e.g. "10.5"
   * @param {string} p.endsAt offer end DATE + TIME, from `formatOfferEnd`
   */
  checkoutOfferLine: ({ isHebrew, discountPercent, endsAt }) =>
    isHebrew
      ? `הטבת שדרוג מוקדם: ${discountPercent}% הנחה על החודש הראשון, בתוקף עד ${endsAt}. מהחודש השני - מחיר מלא.`
      : `Early upgrade offer: ${discountPercent}% off your first month, valid until ${endsAt}. From the second month - full price.`,
};

/**
 * CD-10 (win-back active line) is NOT here: it is the already-approved, already
 * live string in `pages/Payment.js`. Only its TRIGGER moved (the server's
 * `discount_kind`, instead of decoding the token in the browser).
 *
 * CD-11 is "offer unavailable" and has no copy by design - the surfaces are
 * fail-closed and render nothing at all.
 *
 * CD-12 is the checkout's pre-existing `t.errorDefault`, unchanged.
 */

/**
 * TWO FORMATTERS, and which string takes which - this is the thing to get right
 * when a new surface is added.
 *
 *   CD-09 (checkout)      -> `formatOfferEnd`      "24 בספטמבר בשעה 12:00"
 *                                                 "24 September at 12:00"
 *   CD-02 / CD-07 (banner)-> `formatOfferEndDate`  "24 בספטמבר" / "24 September"
 *
 * The approved wording is what decides, and it says the same thing in both
 * languages: CD-09 spells "בתוקף עד {date} בשעה {time}" / "valid until {date}
 * at {time}" and has a `{time}` field; CD-02 and CD-07 say only "עד {date}" /
 * "by {date}" and have none. Feeding the date+time string to a banner prints a
 * time the approved sentence never asked for; feeding the date-only string to
 * the checkout drops a field the approved sentence does ask for. Both
 * formatters pin Asia/Jerusalem in BOTH locales and both return "" on a
 * missing/unparseable value, so a caller can always drop the whole clause
 * rather than print "Invalid Date".
 */

/**
 * Format a discount percent for display.
 *
 * The rule is `1.5% x whole trial days left`, so HALF percents are normal
 * (7 days -> 10.5%, 13 days -> 19.5%) and must never be rounded to a whole
 * number: the checkout's struck price is derived from the exact percent, and a
 * "10%" label above a 10.5% markdown is a claim that contradicts the price
 * beside it. One decimal, trailing `.0` dropped.
 */
export const formatDiscountPercent = (percent) => {
  const value = Number(percent);
  if (!Number.isFinite(value)) return "";
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

/**
 * Shared body of the two offer-end formatters.
 *
 * ALWAYS EXPLICIT about the zone (CLAUDE.md `app/core/timezones.py` rule, the
 * browser half of it): the server sends a UTC instant, the reader is in Israel,
 * and a device whose clock or zone is wrong must not move a deadline we state
 * in writing. Returns "" for a missing/unparseable value so a caller can drop
 * the whole clause rather than print "Invalid Date".
 *
 * The two public formatters differ ONLY in the fields they ask for, so the
 * zone pinning and the ""-on-unparseable contract cannot drift apart between
 * the checkout and the banners.
 */
const formatOfferEndWith = (isoString, isHebrew, fields) => {
  if (!isoString) return "";
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(isHebrew ? "he-IL" : "en-GB", {
      timeZone: "Asia/Jerusalem",
      ...fields,
    }).format(date);
  } catch (error) {
    return "";
  }
};

/**
 * Offer end as DATE + TIME - "24 בספטמבר בשעה 12:00".
 *
 * For CD-09 only, whose approved wording carries both a `{date}` and a
 * `{time}` field. See the "TWO FORMATTERS" note above.
 */
export const formatOfferEnd = (isoString, isHebrew) =>
  formatOfferEndWith(isoString, isHebrew, {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

/**
 * Offer end as a DATE only - "24 בספטמבר".
 *
 * For CD-02 and CD-07, whose approved wording names a date and no time. No
 * year: the offer can never end more than a trial length away, so a year is
 * noise in a one-line banner rather than information.
 */
export const formatOfferEndDate = (isoString, isHebrew) =>
  formatOfferEndWith(isoString, isHebrew, {
    day: "numeric",
    month: "long",
  });
